import React, { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/components/auth/AuthProvider";
import { EnhancedLead } from "@/hooks/use-dashboard-data";
import { toast } from "sonner";
import {
  ClipboardCheck,
  HelpCircle,
  BarChart3,
  Star,
  ChevronDown,
  ChevronUp,
  Save,
  Printer,
  CheckCircle2,
  Circle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Crown, ArrowRight } from "lucide-react";

/* ═══════════════════════════════════
   VISIT CHECKLIST DATA
   ═══════════════════════════════════ */
const CHECKLIST_CATEGORIES = [
  {
    name: "First Impressions",
    items: [
      "Clean, fresh-smelling entrance",
      "Friendly, welcoming staff",
      "Residents appear well-groomed and content",
      "Notice board with activities & menus",
      "Comfortable communal areas",
    ],
  },
  {
    name: "Care Quality",
    items: [
      "Nurse-to-resident ratio explained",
      "GP visits regularly / on-call access",
      "Medication management process clear",
      "Specialist care available (dementia, palliative)",
      "Individual care plans discussed",
      "Night-time staffing levels adequate",
    ],
  },
  {
    name: "Rooms & Facilities",
    items: [
      "Room size suitable, well-lit",
      "En-suite bathroom available",
      "Personal belongings allowed",
      "Call bell accessible from bed",
      "Wheelchair accessible throughout",
      "Garden / outdoor area available",
    ],
  },
  {
    name: "Daily Life",
    items: [
      "Activities schedule varied & engaging",
      "Meals look appetising and nutritious",
      "Dietary needs accommodated",
      "Visiting hours flexible",
      "Outings organised",
      "Hairdresser / barber visits",
    ],
  },
  {
    name: "Safety & Standards",
    items: [
      "HIQA registration confirmed",
      "Recent inspection report reviewed",
      "Fire safety measures visible",
      "Anti-fall measures in place",
      "Infection control protocols",
      "Complaints procedure explained",
    ],
  },
  {
    name: "Costs & Admin",
    items: [
      "Fair Deal scheme accepted",
      "Full cost breakdown provided",
      "Additional charges explained",
      "Trial / respite stay available",
      "Contract terms reviewed",
      "Notice period for leaving clear",
    ],
  },
];

/* ═══════════════════════════════════
   QUESTIONS TO ASK
   ═══════════════════════════════════ */
const QUESTIONS_CATEGORIES = [
  {
    name: "Care & Medical",
    questions: [
      "What is the nurse-to-resident ratio during day and night?",
      "How do you handle medical emergencies?",
      "Can my loved one keep their current GP?",
      "How are medications managed and reviewed?",
      "What dementia / specialist care training do staff receive?",
      "How often are care plans reviewed?",
    ],
  },
  {
    name: "Daily Life & Activities",
    questions: [
      "What does a typical day look like for a resident?",
      "What social activities and outings are available?",
      "Can residents eat in their room if they prefer?",
      "Are there religious services available?",
      "Can family join for meals?",
      "Is there a residents' committee?",
    ],
  },
  {
    name: "Practicalities",
    questions: [
      "What are the visiting hours?",
      "Can personal furniture / belongings be brought in?",
      "Is there WiFi and TV in rooms?",
      "What laundry arrangements are in place?",
      "Is transport available for hospital appointments?",
      "What happens if their needs increase over time?",
    ],
  },
  {
    name: "Costs & Contracts",
    questions: [
      "What is the weekly / monthly cost?",
      "What is included and what costs extra?",
      "Do you accept the Fair Deal / Nursing Homes Support Scheme?",
      "Is a deposit required?",
      "What is the notice period if we need to leave?",
      "Can we do a trial / respite stay first?",
    ],
  },
];

/* ═══════════════════════════════════
   DECISION MATRIX
   ═══════════════════════════════════ */
const DECISION_CRITERIA = [
  { key: "location", label: "Location & Accessibility", weight: 3 },
  { key: "care_quality", label: "Care Quality & Staffing", weight: 5 },
  { key: "facilities", label: "Rooms & Facilities", weight: 3 },
  { key: "activities", label: "Activities & Social Life", weight: 2 },
  { key: "cost", label: "Cost & Value", weight: 4 },
  { key: "feeling", label: "Overall Feeling & Warmth", weight: 4 },
  { key: "safety", label: "Safety & HIQA Standards", weight: 4 },
  { key: "availability", label: "Bed Availability & Wait Time", weight: 3 },
];

/* ═══════════════════════════════════
   VISIT CHECKLIST TAB
   ═══════════════════════════════════ */
function VisitChecklist({ leads }: { leads: EnhancedLead[] }) {
  const { user } = useAuth();
  const [selectedHome, setSelectedHome] = useState<string | null>(leads[0]?.nursing_home_id || null);
  const [checkedItems, setCheckedItems] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);

  const selectedLead = leads.find((l) => l.nursing_home_id === selectedHome);
  const totalItems = CHECKLIST_CATEGORIES.reduce((sum, cat) => sum + cat.items.length, 0);
  const progress = totalItems > 0 ? Math.round((checkedItems.size / totalItems) * 100) : 0;

  // Load saved checklist
  useEffect(() => {
    if (!user || !selectedHome) return;
    const load = async () => {
      const { data } = await supabase
        .from('visit_checklists')
        .select('checklist_data')
        .eq('user_id', user.id)
        .eq('nursing_home_id', selectedHome)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (data?.checklist_data && typeof data.checklist_data === 'object') {
        const saved = data.checklist_data as { checked?: string[] };
        if (saved.checked) setCheckedItems(new Set(saved.checked));
        else setCheckedItems(new Set());
      } else {
        setCheckedItems(new Set());
      }
    };
    load();
  }, [user, selectedHome]);

  const toggleItem = (item: string) => {
    setCheckedItems((prev) => {
      const next = new Set(prev);
      if (next.has(item)) next.delete(item);
      else next.add(item);
      return next;
    });
  };

  const handleSave = async () => {
    if (!user || !selectedHome || !selectedLead) return;
    setSaving(true);
    const checklistData = { checked: Array.from(checkedItems) };

    // Upsert
    const { data: existing } = await supabase
      .from('visit_checklists')
      .select('id')
      .eq('user_id', user.id)
      .eq('nursing_home_id', selectedHome)
      .limit(1)
      .maybeSingle();

    if (existing) {
      await supabase
        .from('visit_checklists')
        .update({ checklist_data: checklistData, overall_score: progress } as any)
        .eq('id', existing.id);
    } else {
      await supabase
        .from('visit_checklists')
        .insert({
          user_id: user.id,
          nursing_home_id: selectedHome,
          nursing_home_name: selectedLead.nursing_home_name,
          checklist_data: checklistData,
          overall_score: progress,
        } as any);
    }

    setSaving(false);
    toast.success('Checklist saved!');
  };

  return (
    <div className="space-y-4">
      {/* Home selector */}
      {leads.length > 1 && (
        <select
          value={selectedHome || ''}
          onChange={(e) => setSelectedHome(e.target.value)}
          className="w-full border rounded-md p-2 text-sm bg-background"
        >
          {leads.map((l) => (
            <option key={l.nursing_home_id} value={l.nursing_home_id}>
              {l.nursing_home_name}
            </option>
          ))}
        </select>
      )}

      {/* Progress */}
      <div className="flex items-center gap-3">
        <Progress value={progress} className="flex-1 h-2" />
        <span className="text-sm font-medium text-muted-foreground">{progress}%</span>
      </div>

      {/* Categories */}
      <div className="space-y-4">
        {CHECKLIST_CATEGORIES.map((cat) => {
          const catChecked = cat.items.filter((i) => checkedItems.has(i)).length;
          return (
            <div key={cat.name} className="border rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-semibold">{cat.name}</h4>
                <span className="text-xs text-muted-foreground">
                  {catChecked}/{cat.items.length}
                </span>
              </div>
              <div className="space-y-1.5">
                {cat.items.map((item) => (
                  <label
                    key={item}
                    className="flex items-center gap-2 text-sm cursor-pointer hover:bg-muted/30 rounded px-1 py-0.5"
                  >
                    <button
                      onClick={() => toggleItem(item)}
                      className="shrink-0"
                    >
                      {checkedItems.has(item) ? (
                        <CheckCircle2 size={16} className="text-primary" />
                      ) : (
                        <Circle size={16} className="text-muted-foreground/40" />
                      )}
                    </button>
                    <span className={cn(checkedItems.has(item) && "line-through text-muted-foreground")}>
                      {item}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex gap-2">
        <Button onClick={handleSave} disabled={saving} className="gap-2">
          <Save size={14} />
          {saving ? "Saving..." : "Save Checklist"}
        </Button>
        <Button variant="outline" className="gap-2" onClick={() => window.print()}>
          <Printer size={14} />
          Print
        </Button>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════
   QUESTIONS TO ASK TAB
   ═══════════════════════════════════ */
function QuestionsToAsk() {
  const [expandedCat, setExpandedCat] = useState<string | null>(QUESTIONS_CATEGORIES[0].name);

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        Use these questions when visiting or calling nursing homes. Tap to expand each category.
      </p>
      {QUESTIONS_CATEGORIES.map((cat) => (
        <div key={cat.name} className="border rounded-lg overflow-hidden">
          <button
            onClick={() => setExpandedCat(expandedCat === cat.name ? null : cat.name)}
            className="w-full flex items-center justify-between p-3 hover:bg-muted/30"
          >
            <span className="text-sm font-semibold">{cat.name}</span>
            {expandedCat === cat.name ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
          {expandedCat === cat.name && (
            <div className="px-3 pb-3 space-y-2">
              {cat.questions.map((q, i) => (
                <div key={i} className="flex items-start gap-2 text-sm">
                  <HelpCircle size={14} className="text-primary shrink-0 mt-0.5" />
                  <span>{q}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
      <Button variant="outline" className="gap-2" onClick={() => window.print()}>
        <Printer size={14} />
        Print Questions
      </Button>
    </div>
  );
}

/* ═══════════════════════════════════
   DECISION MATRIX TAB
   ═══════════════════════════════════ */
function DecisionMatrix({ leads }: { leads: EnhancedLead[] }) {
  const [scores, setScores] = useState<Record<string, Record<string, number>>>({});

  const setScore = (homeId: string, criterion: string, score: number) => {
    setScores((prev) => ({
      ...prev,
      [homeId]: { ...(prev[homeId] || {}), [criterion]: score },
    }));
  };

  const getWeightedTotal = (homeId: string) => {
    const homeScores = scores[homeId] || {};
    let total = 0;
    let maxTotal = 0;
    DECISION_CRITERIA.forEach((c) => {
      total += (homeScores[c.key] || 0) * c.weight;
      maxTotal += 5 * c.weight;
    });
    return maxTotal > 0 ? Math.round((total / maxTotal) * 100) : 0;
  };

  const sortedLeads = [...leads].sort((a, b) => getWeightedTotal(b.nursing_home_id) - getWeightedTotal(a.nursing_home_id));

  if (leads.length < 2) {
    return (
      <div className="text-center py-8">
        <BarChart3 className="h-10 w-10 text-muted-foreground/40 mx-auto mb-3" />
        <p className="text-sm text-muted-foreground">
          Add at least 2 nursing homes to your tracker to compare them side by side.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Rate each nursing home from 1–5 on each criterion. Weighted scores help you compare objectively.
      </p>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b">
              <th className="text-left py-2 pr-3 font-medium text-muted-foreground min-w-[160px]">Criterion</th>
              {sortedLeads.slice(0, 4).map((lead) => (
                <th key={lead.nursing_home_id} className="text-center py-2 px-2 font-medium min-w-[100px]">
                  <span className="truncate block max-w-[100px]">{lead.nursing_home_name}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {DECISION_CRITERIA.map((criterion) => (
              <tr key={criterion.key} className="border-b">
                <td className="py-2 pr-3">
                  <span className="font-medium">{criterion.label}</span>
                  <span className="text-xs text-muted-foreground ml-1">(×{criterion.weight})</span>
                </td>
                {sortedLeads.slice(0, 4).map((lead) => (
                  <td key={lead.nursing_home_id} className="text-center py-2 px-2">
                    <div className="flex items-center justify-center gap-0.5">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <button
                          key={n}
                          onClick={() => setScore(lead.nursing_home_id, criterion.key, n)}
                          className="p-0.5"
                        >
                          <Star
                            size={14}
                            className={cn(
                              "transition-colors",
                              (scores[lead.nursing_home_id]?.[criterion.key] || 0) >= n
                                ? "text-amber-400 fill-amber-400"
                                : "text-muted-foreground/30"
                            )}
                          />
                        </button>
                      ))}
                    </div>
                  </td>
                ))}
              </tr>
            ))}
            {/* Total row */}
            <tr className="bg-muted/30">
              <td className="py-3 pr-3 font-semibold">Weighted Score</td>
              {sortedLeads.slice(0, 4).map((lead) => {
                const total = getWeightedTotal(lead.nursing_home_id);
                return (
                  <td key={lead.nursing_home_id} className="text-center py-3 px-2">
                    <span className={cn(
                      "text-lg font-bold",
                      total >= 70 ? "text-green-600" : total >= 40 ? "text-amber-600" : "text-muted-foreground"
                    )}>
                      {total}%
                    </span>
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════
   MAIN PANEL
   ═══════════════════════════════════ */
interface PremiumToolsPanelProps {
  leads: EnhancedLead[];
  isPremium: boolean;
}

export function PremiumToolsPanel({ leads, isPremium }: PremiumToolsPanelProps) {
  return (
    <Card className="relative overflow-hidden">
      {!isPremium && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/80 backdrop-blur-[2px] rounded-lg">
          <div className="text-center px-6 py-4">
            <Crown className="h-8 w-8 text-amber-500 mx-auto mb-2" />
            <p className="font-semibold text-sm">Premium Feature</p>
            <p className="text-xs text-muted-foreground mt-1 max-w-[220px]">
              Unlock checklists, comparison tools, and expert questions with Premium.
            </p>
            <a
              href="/premium"
              className="inline-flex items-center gap-1 mt-3 text-xs font-medium text-primary hover:underline"
            >
              Upgrade to Premium <ArrowRight size={12} />
            </a>
          </div>
        </div>
      )}
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <ClipboardCheck size={18} className="text-primary" />
          Care Evaluation Tools
          {!isPremium && <Badge variant="outline" className="text-[10px] gap-1 border-amber-300 text-amber-700 bg-amber-50"><Crown size={10} />Premium</Badge>}
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Checklists, questions, and comparison tools to help you choose the right home
        </p>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="checklist">
          <TabsList className="w-full grid grid-cols-3">
            <TabsTrigger value="checklist" className="text-xs gap-1">
              <ClipboardCheck size={14} />
              Visit Checklist
            </TabsTrigger>
            <TabsTrigger value="questions" className="text-xs gap-1">
              <HelpCircle size={14} />
              Questions
            </TabsTrigger>
            <TabsTrigger value="matrix" className="text-xs gap-1">
              <BarChart3 size={14} />
              Compare
            </TabsTrigger>
          </TabsList>

          <TabsContent value="checklist" className="mt-4">
            <VisitChecklist leads={leads} />
          </TabsContent>

          <TabsContent value="questions" className="mt-4">
            <QuestionsToAsk />
          </TabsContent>

          <TabsContent value="matrix" className="mt-4">
            <DecisionMatrix leads={leads} />
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
